import { useState, useEffect, useCallback } from "react";
import { 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  Copy, 
  Check,
  Loader2,
  Search,
  X,
  FileImage,
  Link2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { API_URL } from "../utils/api";

export default function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/media`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setFiles(data.files || []);
      }
    } catch (error) {
      toast.error("Kunde inte hämta bilder");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    
    setUploading(true);
    
    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('alt_text', file.name.split('.')[0]);
        formData.append('description', '');

        const res = await fetch(`${API_URL}/api/admin/media/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        const data = await res.json();
        
        if (data.success) {
          toast.success(`${file.name} uppladdad!`);
          setFiles(prev => [data.file, ...prev]);
        } else {
          toast.error(data.detail || `Kunde inte ladda upp ${file.name}`);
        }
      } catch (error) {
        toast.error(`Fel vid uppladdning av ${file.name}`);
      }
    }
    
    setUploading(false);
  };

  const handleDelete = async (fileId) => {
    if (!confirm("Är du säker på att du vill radera denna bild?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/media/${fileId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success("Bilden raderades");
        setFiles(prev => prev.filter(f => f.id !== fileId));
        if (selectedFile?.id === fileId) {
          setSelectedFile(null);
        }
      } else {
        toast.error(data.detail || "Kunde inte radera bilden");
      }
    } catch (error) {
      toast.error("Fel vid radering");
    }
  };

  const copyUrl = (file) => {
    const url = `${API_URL}/api/media/${file.id}/download`;
    navigator.clipboard.writeText(url);
    setCopiedId(file.id);
    toast.success("URL kopierad!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const filteredFiles = files.filter(file => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      file.original_filename?.toLowerCase().includes(query) ||
      file.alt_text?.toLowerCase().includes(query) ||
      file.description?.toLowerCase().includes(query)
    );
  });

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mediabibliotek</h1>
          <p className="text-gray-500 mt-1">Ladda upp och hantera bilder för din webbplats</p>
        </div>
        <div className="text-sm text-gray-500">
          {files.length} bild{files.length !== 1 ? 'er' : ''}
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragActive 
            ? 'border-[#255E69] bg-[#255E69]/5' 
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          className="hidden"
          accept="image/*"
          multiple
          onChange={(e) => handleUpload(e.target.files)}
        />
        
        <div className="flex flex-col items-center gap-3">
          {uploading ? (
            <>
              <Loader2 className="w-10 h-10 text-[#255E69] animate-spin" />
              <p className="text-gray-600">Laddar upp...</p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                <Upload className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <label 
                  htmlFor="file-upload"
                  className="text-[#255E69] font-medium cursor-pointer hover:underline"
                >
                  Klicka för att ladda upp
                </label>
                <span className="text-gray-500"> eller dra och släpp</span>
              </div>
              <p className="text-sm text-gray-400">PNG, JPG, GIF, WebP, SVG (max 10MB)</p>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Sök bilder..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Files Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#255E69] animate-spin" />
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FileImage className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {searchQuery ? "Inga bilder matchar sökningen" : "Inga bilder uppladdade ännu"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              className={`group relative bg-white rounded-xl border overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
                selectedFile?.id === file.id ? 'ring-2 ring-[#255E69]' : 'border-gray-200'
              }`}
              onClick={() => setSelectedFile(selectedFile?.id === file.id ? null : file)}
            >
              {/* Image */}
              <div className="aspect-square bg-gray-100 overflow-hidden">
                <img
                  src={`${API_URL}/api/media/${file.id}/download`}
                  alt={file.alt_text || file.original_filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyUrl(file);
                  }}
                >
                  {copiedId === file.id ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(file.id);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* File info */}
              <div className="p-2">
                <p className="text-xs font-medium text-gray-700 truncate">
                  {file.original_filename}
                </p>
                <p className="text-xs text-gray-400">
                  {formatFileSize(file.size)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected file details */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedFile(null)}>
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview */}
            <div className="bg-gray-100 flex items-center justify-center p-4" style={{ maxHeight: '400px' }}>
              <img
                src={`${API_URL}/api/media/${selectedFile.id}/download`}
                alt={selectedFile.alt_text || selectedFile.original_filename}
                className="max-w-full max-h-[350px] object-contain rounded-lg"
              />
            </div>
            
            {/* Details */}
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedFile.original_filename}</h3>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(selectedFile.size)} • {formatDate(selectedFile.created_at)}
                  </p>
                </div>
                <button 
                  onClick={() => setSelectedFile(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* URL */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Bild-URL (för användning på sidor)
                </label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${API_URL}/api/media/${selectedFile.id}/download`}
                    className="text-sm bg-gray-50"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyUrl(selectedFile)}
                    className="shrink-0"
                  >
                    {copiedId === selectedFile.id ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Kopiera denna URL och använd den i dina SEO-sidor eller bloggposter
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSelectedFile(null)}
                >
                  Stäng
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    handleDelete(selectedFile.id);
                    setSelectedFile(null);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Radera
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
